'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter, useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import { useLang } from '@/lib/langContext';
import { t } from '@/lib/i18n';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';

interface SignInValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface Props {
  onSwitchToSignUp: () => void;
}

export default function SignInForm({ onSwitchToSignUp }: Props) {
  const { lang } = useLang();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<SignInValues>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const onSubmit = async (data: SignInValues) => {
    setLoading(true);
    const supabase = createClient();

    const { data: authData, error } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });
    setLoading(false);

    if (error || !authData?.user) {
      toast.error(error?.message || t(lang, 'invalidCredentials'));
      return;
    }

    // Check if user already belongs to a workspace → skip onboarding
    const { data: membership } = await supabase
      .from('workspace_members')
      .select('workspace_id')
      .eq('user_id', authData.user.id)
      .maybeSingle();

    const redirect = searchParams?.get('redirect');
    if (redirect && redirect.startsWith('/')) {
      router.push(redirect);
    } else if (!membership) {
      router.push('/onboarding');
    } else {
      router.push('/dashboard');
    }
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    const supabase = createClient();
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    setGoogleLoading(false);
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[hsl(var(--foreground))] mb-2">{t(lang, 'signIn')}</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          {t(lang, 'dontHaveAccount')}{' '}
          <button onClick={onSwitchToSignUp} className="text-navy-600 font-medium hover:underline">
            {t(lang, 'signUp')}
          </button>
        </p>
      </div>

      <button
        onClick={handleGoogle}
        disabled={googleLoading}
        className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-btn border border-[hsl(var(--border))] bg-white text-sm font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] active:scale-[0.99] transition-all duration-150 mb-4"
      >
        {googleLoading ? (
          <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
        )}
        {t(lang, 'continueWithGoogle')}
      </button>

      <div className="flex items-center gap-3 mb-4">
        <hr className="flex-1 border-[hsl(var(--border))]" />
        <span className="text-xs text-[hsl(var(--muted-foreground))]">{t(lang, 'orContinueWith')}</span>
        <hr className="flex-1 border-[hsl(var(--border))]" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-1.5">
            {t(lang, 'email')}
          </label>
          <input
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            {...register('email', {
              required: t(lang, 'emailRequired'),
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t(lang, 'emailInvalid') },
            })}
            className={`w-full px-3 py-2.5 rounded-btn border text-sm bg-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-navy-400 focus:ring-offset-1
              ${errors.email ? 'border-red-400 bg-red-50' : 'border-[hsl(var(--border))] hover:border-navy-200'}`}
          />
          {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-medium text-[hsl(var(--foreground))]">
              {t(lang, 'password')}
            </label>
            <button type="button" className="text-xs text-navy-600 hover:underline">{t(lang, 'forgotPassword')}</button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              {...register('password', {
                required: t(lang, 'passwordRequired'),
                minLength: { value: 8, message: t(lang, 'passwordMin') },
              })}
              className={`w-full px-3 py-2.5 pe-10 rounded-btn border text-sm bg-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-navy-400 focus:ring-offset-1
                ${errors.password ? 'border-red-400 bg-red-50' : 'border-[hsl(var(--border))] hover:border-navy-200'}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(s => !s)}
              className="absolute inset-y-0 end-0 flex items-center pe-3 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" {...register('rememberMe')} className="rounded border-[hsl(var(--border))] text-navy-600 focus:ring-navy-400" />
          <span className="text-sm text-[hsl(var(--muted-foreground))]">{t(lang, 'rememberMe')}</span>
        </label>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-btn bg-navy-600 text-white text-sm font-semibold hover:bg-navy-700 active:scale-[0.99] transition-all duration-150 disabled:opacity-70"
          style={{ minWidth: '120px' }}
        >
          {loading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              {t(lang, 'signingIn')}
            </>
          ) : t(lang, 'signIn')}
        </button>
      </form>
    </div>
  );
}